/**
 * Report Delivery Analytics Service
 * Fetches TAT-based delivery analytics from RIS backend.
 */

import { API_ENDPOINTS } from '../config/api.config';
import { apiGet } from '../utils/apiClient';

export interface ReportDeliveryChartPoint {
  date: string;
  minutes: number;
  reportCount?: number;
}

export interface ReportDeliveryModalityRow {
  modality: string;
  totalReports: number;
  avgDeliveryTimeMinutes: number;
  avgDeliveryTimeFormatted: string;
}

/** Per-study row for report delivery analytics export. */
export interface ReportDeliveryDetailRow {
  uhid: string;
  patientVisitId: string;
  modality: string;
  studyAllocationTime: string | null;
  reportDraftTime: string | null;
  approvalTime: string | null;
  dispatchTime: string | null;
  totalDeliveryTatMinutes: number | null;
  totalDeliveryTatFormatted: string;
  endToEndTatMinutes: number | null;
  endToEndTatFormatted: string;
}

export interface ReportDeliverySummary {
  totalStudies: number;
  totalPending: number;
  totalReportsDelivered: number;
  averageDeliveryTimeMinutes: number;
  averageDeliveryTimeFormatted: string;
  delayedReports: number;
  pendingOverdue: number;
  lateDelivered: number;
}

export interface ReportDeliveryAnalyticsData {
  summary: ReportDeliverySummary;
  chartPoints: ReportDeliveryChartPoint[];
  modalityRows: ReportDeliveryModalityRow[];
  detailRows: ReportDeliveryDetailRow[];
  meta?: {
    excludedInvalidAccession?: number;
    warnings?: string[];
  };
}

export interface ReportDeliveryFilters {
  startDate: string;
  endDate: string;
  modality?: string;
  priority?: string;
  facilityId?: string;
  radiologistId?: string;
  channel?: string;
}

export interface ReportDeliveryServiceResponse {
  success: boolean;
  data?: ReportDeliveryAnalyticsData;
  error?: string;
  message?: string;
}

function num(v: unknown): number {
  return typeof v === 'number' && !Number.isNaN(v) ? v : 0;
}

function str(v: unknown, fallback = ''): string {
  return v != null ? String(v) : fallback;
}

function normalizeSummary(raw: Record<string, unknown>): ReportDeliverySummary {
  return {
    totalStudies: num(raw.totalStudies ?? raw.total_studies),
    totalPending: num(raw.totalPending ?? raw.total_pending),
    totalReportsDelivered: num(raw.totalReportsDelivered ?? raw.total_reports_delivered),
    averageDeliveryTimeMinutes: num(
      raw.averageDeliveryTimeMinutes ?? raw.average_delivery_time_minutes
    ),
    averageDeliveryTimeFormatted: str(
      raw.averageDeliveryTimeFormatted ?? raw.average_delivery_time_formatted,
      '0 min'
    ),
    delayedReports: num(raw.delayedReports ?? raw.delayed_reports),
    pendingOverdue: num(raw.pendingOverdue ?? raw.pending_overdue),
    lateDelivered: num(raw.lateDelivered ?? raw.late_delivered),
  };
}

function normalizeChartPoints(raw: unknown): ReportDeliveryChartPoint[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(item => {
    const row = (item as Record<string, unknown>) || {};
    return {
      date: str(row.date),
      minutes: num(row.minutes),
      reportCount: num(row.reportCount ?? row.report_count),
    };
  });
}

function nullableTimestamp(v: unknown): string | null {
  if (typeof v !== 'string' && typeof v !== 'number') {
    return null;
  }
  const text = String(v).trim();
  if (text === '') {
    return null;
  }
  return text;
}

function normalizeDetailRows(raw: unknown): ReportDeliveryDetailRow[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(item => {
    const row = (item as Record<string, unknown>) || {};
    const totalDeliveryTatMinutes = row.totalDeliveryTatMinutes ?? row.total_delivery_tat_minutes;
    const endToEndTatMinutes = row.endToEndTatMinutes ?? row.end_to_end_tat_minutes;
    return {
      uhid: str(row.uhid),
      patientVisitId: str(row.patientVisitId ?? row.patient_visit_id),
      modality: str(row.modality),
      studyAllocationTime: nullableTimestamp(row.studyAllocationTime ?? row.study_allocation_time),
      reportDraftTime: nullableTimestamp(row.reportDraftTime ?? row.report_draft_time),
      approvalTime: nullableTimestamp(row.approvalTime ?? row.approval_time),
      dispatchTime: nullableTimestamp(row.dispatchTime ?? row.dispatch_time),
      totalDeliveryTatMinutes:
        totalDeliveryTatMinutes != null && totalDeliveryTatMinutes !== ''
          ? num(totalDeliveryTatMinutes)
          : null,
      totalDeliveryTatFormatted: str(
        row.totalDeliveryTatFormatted ?? row.total_delivery_tat_formatted,
        '—'
      ),
      endToEndTatMinutes:
        endToEndTatMinutes != null && endToEndTatMinutes !== '' ? num(endToEndTatMinutes) : null,
      endToEndTatFormatted: str(row.endToEndTatFormatted ?? row.end_to_end_tat_formatted, '—'),
    };
  });
}

function normalizeModalityRows(raw: unknown): ReportDeliveryModalityRow[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(item => {
    const row = (item as Record<string, unknown>) || {};
    const avgMinutes = num(row.avgDeliveryTimeMinutes ?? row.avg_delivery_time_minutes);
    return {
      modality: str(row.modality),
      totalReports: num(row.totalReports ?? row.total_reports),
      avgDeliveryTimeMinutes: avgMinutes,
      avgDeliveryTimeFormatted: str(
        row.avgDeliveryTimeFormatted ?? row.avg_delivery_time_formatted,
        avgMinutes > 0 ? `${avgMinutes} min` : '—'
      ),
    };
  });
}

function normalizeAnalyticsData(raw: Record<string, unknown>): ReportDeliveryAnalyticsData {
  const summaryRaw = (raw.summary as Record<string, unknown>) || {};
  const metaRaw = (raw.meta as Record<string, unknown>) || {};

  return {
    summary: normalizeSummary(summaryRaw),
    chartPoints: normalizeChartPoints(raw.chartPoints ?? raw.chart_points),
    modalityRows: normalizeModalityRows(raw.modalityRows ?? raw.modality_rows),
    detailRows: normalizeDetailRows(raw.detailRows ?? raw.detail_rows),
    meta: {
      excludedInvalidAccession: num(
        metaRaw.excludedInvalidAccession ?? metaRaw.excluded_invalid_accession
      ),
      warnings: Array.isArray(metaRaw.warnings) ? metaRaw.warnings.map(w => String(w)) : [],
    },
  };
}

export async function getReportDeliveryAnalytics(
  filters: ReportDeliveryFilters
): Promise<ReportDeliveryServiceResponse> {
  try {
    const params = new URLSearchParams();
    params.set('start_date', filters.startDate);
    params.set('end_date', filters.endDate);
    if (filters.modality) {
      params.set('modality', filters.modality);
    }
    if (filters.priority) {
      params.set('priority', filters.priority);
    }
    if (filters.facilityId) {
      params.set('facility_id', filters.facilityId);
    }
    if (filters.radiologistId) {
      params.set('radiologist_id', filters.radiologistId);
    }
    if (filters.channel) {
      params.set('channel', filters.channel);
    }

    const url = `${API_ENDPOINTS.ANALYTICS.REPORT_DELIVERY}?${params.toString()}`;
    const response = await apiGet<{
      success?: boolean;
      data?: Record<string, unknown>;
      error?: string;
      message?: string;
    }>(url, { requireAuth: true });

    const apiData = response.data;
    const payload = apiData?.data;

    if (payload && typeof payload === 'object' && apiData?.success !== false) {
      return {
        success: true,
        data: normalizeAnalyticsData(payload),
        message: apiData?.message,
      };
    }

    return {
      success: false,
      error: apiData?.error || 'Invalid response format',
    };
  } catch (err) {
    const message =
      err instanceof Error ? err.message : 'Failed to fetch report delivery analytics';
    return {
      success: false,
      error: message,
    };
  }
}
